import { cnpj } from "cpf-cnpj-validator";

/**
 * CNPJ Manager - Gerenciador centralizado para tratamento de CNPJ
 *
 * Suporta o novo formato alfanumérico válido a partir de julho/2026:
 * - Formato: AA.AAA.AAA/AAAA-DV (14 caracteres)
 * - 12 primeiros: alfanuméricos (A-Z, 0-9)
 * - 2 últimos (DV): apenas numéricos (0-9)
 * - Mantém retrocompatibilidade com CNPJs numéricos existentes
 */

const CNPJ_MASK = "**.***.***/****-99";

// Lista de CNPJs inválidos (sequências repetidas)
const BLACKLIST = [
  "00000000000000",
  "11111111111111",
  "22222222222222",
  "33333333333333",
  "44444444444444",
  "55555555555555",
  "66666666666666",
  "77777777777777",
  "88888888888888",
  "99999999999999",
];

/**
 * Remove formatação do CNPJ, mantendo apenas letras e números
 * Converte para maiúsculas para padronização
 * @param {string} value - CNPJ formatado ou não
 * @returns {string} CNPJ sem formatação em maiúsculas
 */
export const stripCNPJ = (value) => {
  if (!value) return "";
  return value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
};

/**
 * Normaliza CNPJ para maiúsculas (sem remover formatação)
 * @param {string} value - CNPJ
 * @returns {string} CNPJ em maiúsculas
 */
export const normalizeCNPJ = (value) => {
  return value ? value.toUpperCase() : "";
};

/**
 * Formata CNPJ no padrão AA.AAA.AAA/AAAA-DV
 * Suporta tanto CNPJs numéricos quanto alfanuméricos
 * @param {string} value - CNPJ sem formatação
 * @returns {string} CNPJ formatado
 */
export const formatCNPJ = (value) => {
  const stripped = stripCNPJ(value);
  if (!stripped) return "";

  // Se tiver mais de 14 caracteres, retorna apenas os 14 primeiros formatados
  const normalized = stripped.substring(0, 14);

  // Formata progressivamente conforme o usuário digita
  if (normalized.length <= 2) return normalized;
  if (normalized.length <= 5)
    return `${normalized.slice(0, 2)}.${normalized.slice(2)}`;
  if (normalized.length <= 8)
    return `${normalized.slice(0, 2)}.${normalized.slice(2, 5)}.${normalized.slice(5)}`;
  if (normalized.length <= 12)
    return `${normalized.slice(0, 2)}.${normalized.slice(2, 5)}.${normalized.slice(5, 8)}/${normalized.slice(8)}`;

  // Formato completo: AA.AAA.AAA/AAAA-DV
  return `${normalized.slice(0, 2)}.${normalized.slice(2, 5)}.${normalized.slice(5, 8)}/${normalized.slice(8, 12)}-${normalized.slice(12, 14)}`;
};

/**
 * Valida CNPJ (numérico ou alfanumérico)
 * - CNPJs numéricos: validação completa com dígito verificador
 * - CNPJs alfanuméricos: validação estrutural (aguardando algoritmo oficial)
 * @param {string} value - CNPJ a ser validado
 * @returns {boolean} true se válido
 */
export const validateCNPJ = (value) => {
  if (!value) return false;

  const stripped = stripCNPJ(value);

  // Verifica se tem exatamente 14 caracteres
  if (stripped.length !== 14) return false;

  // Rejeita CNPJs da blacklist (sequências repetidas)
  if (BLACKLIST.includes(stripped)) return false;

  // Valida estrutura: 12 alfanuméricos + 2 numéricos
  const structureRegex = /^[A-Z0-9]{12}[0-9]{2}$/;
  if (!structureRegex.test(stripped)) return false;

  // Se for CNPJ numérico (formato antigo), valida com algoritmo módulo 11
  const isNumeric = /^\d{14}$/.test(stripped);
  if (isNumeric) {
    return cnpj.isValid(stripped);
  }

  // CNPJs alfanuméricos: validação estrutural aprovada
  // (Algoritmo de dígito verificador alfanumérico será implementado quando oficializado)
  return true;
};

/**
 * Verifica se CNPJ é numérico (formato antigo)
 * @param {string} value - CNPJ
 * @returns {boolean} true se for apenas números
 */
export const isNumericCNPJ = (value) => {
  const stripped = stripCNPJ(value);
  return /^\d{14}$/.test(stripped);
};

/**
 * Verifica se CNPJ é alfanumérico (novo formato)
 * @param {string} value - CNPJ
 * @returns {boolean} true se contiver letras
 */
export const isAlphanumericCNPJ = (value) => {
  const stripped = stripCNPJ(value);
  return /[A-Z]/.test(stripped) && stripped.length === 14;
};

/**
 * Retorna a máscara do CNPJ
 * @returns {string} Máscara no formato react-input-mask
 */
export const getCNPJMask = () => CNPJ_MASK;

// Exportação default com todas as funções
const cnpjManager = {
  format: formatCNPJ,
  validate: validateCNPJ,
  strip: stripCNPJ,
  normalize: normalizeCNPJ,
  isNumeric: isNumericCNPJ,
  isAlphanumeric: isAlphanumericCNPJ,
  mask: CNPJ_MASK,
};

export default cnpjManager;
